/**
 * Tiny inline-markdown link parser.
 *
 * The AI tools embed citations as inline markdown links of the form
 * `[title](https://url)` (often wrapped in parentheses, e.g. `([title](url))`)
 * inside otherwise-plain prose. Rather than pull in a full markdown library
 * (heavy, and we only need this one construct) we split a string into plain-text
 * and link segments so the UI can render anchors for the links and keep the rest
 * as text. Only `http(s)` URLs are treated as links; anything else stays literal.
 */

/** A plain run of text with no markup. */
export interface TextSegment {
  type: 'text'
  value: string
}

/** A parsed `[label](href)` markdown link with a safe `http(s)` href. */
export interface LinkSegment {
  type: 'link'
  label: string
  href: string
}

export type MarkdownSegment = TextSegment | LinkSegment

// `[label](href)` — label is anything up to the first `]`, href anything up to
// the closing `)`. Non-greedy so adjacent links don't merge.
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g

/**
 * Split `input` into an ordered list of text/link segments. Returns a single
 * text segment for input with no links (including empty strings), so callers can
 * always map over the result.
 */
export function parseMarkdownLinks(input: string): MarkdownSegment[] {
  const segments: MarkdownSegment[] = []
  let lastIndex = 0

  // `matchAll` keeps the regex stateless per call (no shared lastIndex bugs).
  for (const match of input.matchAll(LINK_RE)) {
    const start = match.index ?? 0
    if (start > lastIndex) {
      segments.push({ type: 'text', value: input.slice(lastIndex, start) })
    }
    segments.push({ type: 'link', label: match[1], href: match[2] })
    lastIndex = start + match[0].length
  }

  if (lastIndex < input.length) {
    segments.push({ type: 'text', value: input.slice(lastIndex) })
  }

  // Guarantee at least one segment so the empty-string case is renderable.
  if (segments.length === 0) {
    segments.push({ type: 'text', value: input })
  }

  return segments
}

/** True when `input` contains at least one inline markdown link. */
export function hasMarkdownLink(input: string): boolean {
  // Fresh regex: the module-level one is `g` and carries `lastIndex` state.
  return /\[[^\]]+\]\(https?:\/\/[^)\s]+\)/.test(input)
}
